// The search box: what it matches, and what it suggests.
//
// Split out of lib/useEvents so it holds no React and no Supabase, which is what
// lets scripts/search-eval.ts run it over the live rows.
//
// TWO RULES SHAPE EVERYTHING BELOW (Andrew, 7/29):
//
//   1. NAMES AND WORDS MATCH, ADDRESSES DO NOT. Half the venue strings end in a
//      street address, so matching the raw column meant typing "1219" listed
//      every event on that block and "Main St" listed most of downtown. Neither
//      is a search anybody performs on purpose, and both bury the thing that was
//      actually typed. The address still SHOWS - it's how you tell two Dragon
//      Fire Games apart - it just isn't a match key.
//
//   2. THE DROPDOWN SUGGESTS SETS, NOT ROWS. Individual events came out of it:
//      a suggestion whose payoff is one night is worth less than the keystrokes
//      it saves, and the events are already listed on the calendar behind the
//      drawer. A place or a category stands for a whole run of nights.

import { hostLabel, hostSearchTerms } from './hostLabels'
import { eventKinds } from './kinds'
import { hostedByNames, venueAddress, venueSearchTerms } from './hostedBy'
import { cityForVenue } from './areas'
import type { EventRow } from '../types'

// "&" and "and" are the same word to whoever is typing. 47 events carry the note
// "Lynchburg Parks & Rec program", so searching "parks and rec" - the way you'd
// say it - matched nothing at all until this (Lisa, 7/28).
export function normalizeForSearch(s: string): string {
  return s
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\s+/g, ' ')
    .trim()
}

const STREET_SUFFIX =
  'St|Street|Ave|Avenue|Rd|Road|Dr|Drive|Ln|Lane|Blvd|Boulevard|Way|Ct|Court|Pl|Place|Pkwy|Hwy|Cir|Circle|Ter|Terrace|Sq|Square'

// A house number followed by a street, plus any suite hanging off it. Notes are
// prose, so the number is what makes this an address rather than a mention: "on
// Main St" stays searchable, "420 Grove St" does not.
const STREET_ADDRESS = new RegExp(
  String.raw`\b\d+\s+(?:[\w.'-]+\s+){0,3}(?:${STREET_SUFFIX})\b\.?(?:\s*(?:Ste|Suite|Unit|Rm|Room|#)\.?\s*[\w-]+)?`,
  'gi',
)

/** Note text with any street address in it removed. Rule 1, applied to prose. */
function scrubAddresses(note: string | null): string {
  if (!note) return ''
  return note.replace(STREET_ADDRESS, ' ').replace(/\b\d{5}(-\d{4})?\b/g, ' ')
}

// Everything a row can be found BY - the address-scrub regex, the hosted-by/
// ALIASES lookup, the kind labels - is a function of the row alone, never of
// what's typed. App.tsx holds `query` in React state and re-renders on every
// keystroke, and until 2026-09-05 this whole list was rebuilt from scratch for
// every row on every one of those renders: typing "karaoke" cost seven full
// passes over the whole loaded set (2874 rows live) where one would do. Andrew
// reported this as the search box itself being slow to type into - it wasn't
// the box, it was this running synchronously in the render that draws the
// keystroke.
//
// `events` only gets a new array (and new row objects) on a refetch, so a
// WeakMap keyed on the row is invalidated for free the moment that happens -
// there's no cache to bust by hand, and a row's own fields never take a second
// pass either.
const searchFieldsCache = new WeakMap<EventRow, string[]>()

function searchFieldsFor(e: EventRow): string[] {
  const cached = searchFieldsCache.get(e)
  if (cached) return cached
  const fields = [
    e.name,
    scrubAddresses(e.note),
    ...venueSearchTerms(e.venue),
    // An organiser layered on top of a venue (lib/hostedBy's ORGANIZERS) has no
    // venue string of its own to be found by - "Hill City Mahj" appears nowhere
    // on a "Tile Tuesday" row at Palmera House except the event's own name
    // pattern, so its name has to be added as a field explicitly or picking its
    // own search suggestion would match nothing.
    ...hostedByNames(e.venue, e.name),
    // BOTH spellings of the category: someone typing what the drawer showed them
    // ("Arts & Crafts") has to find the events stored under 'Art & Craft'.
    ...eventKinds(e).flatMap((k) => hostSearchTerms(k)),
  ]
    .filter((f): f is string => Boolean(f))
    .map((f) => normalizeForSearch(f))
  searchFieldsCache.set(e, fields)
  return fields
}

/**
 * Free-text match over what someone actually has in mind: the event title, the
 * details, the place, and the category. Case- and punctuation-insensitive
 * partial match, so "needle" finds Needle Ninja.
 */
export function matchesQuery(e: EventRow, q: string): boolean {
  // Token-AND across fields, not phrase-in-one-field: "Bower Center stained"
  // mixes a venue ("Bower Center for the Arts") with a title word ("Stained
  // Glass Workshop"), and no single field carries the whole phrase, so it
  // rendered zero results until 2026-08-06 (Andrew). Each word must match
  // SOMEWHERE; different words may land in different fields.
  const tokens = normalizeForSearch(q).split(' ').filter(Boolean)
  if (tokens.length === 0) return true
  const fields = searchFieldsFor(e)
  return tokens.every((t) => fields.some((f) => f.includes(t)))
}

export interface SearchSuggestion {
  value: string
  /** Shown beside it, so a list mixing the two isn't ambiguous. */
  kind: 'Place' | 'Category'
  /** Where a place is - city, and its address when the rows carry one. */
  detail?: string
}

// Same shape of fix as searchFieldsFor above, for suggestSearch's own per-row
// reads: names/city/addr are ROW data, cached once per row rather than redone
// on every keystroke.
interface PlaceDerivation {
  names: string[]
  city: string
  addr: string | null
}
const placeDerivationCache = new WeakMap<EventRow, PlaceDerivation>()

function placeDerivationFor(e: EventRow): PlaceDerivation {
  const cached = placeDerivationCache.get(e)
  if (cached) return cached
  const derived: PlaceDerivation = {
    names: hostedByNames(e.venue, e.name),
    city: cityForVenue(e.venue),
    addr: venueAddress(e.venue),
  }
  placeDerivationCache.set(e, derived)
  return derived
}

/**
 * Things already in the calendar that match what's been typed so far.
 *
 * Only ever suggests text that HAS results, so taking a suggestion can't land on
 * the empty state. Places come before categories: a name is the more specific
 * thing to have half-typed.
 *
 * Two characters before it says anything: on a single letter nearly everything
 * matches and the list is noise.
 */
export function suggestSearch(
  events: EventRow[],
  q: string,
  limit = 6,
): SearchSuggestion[] {
  const needle = normalizeForSearch(q)
  if (needle.length < 2) return []

  // One row per BUSINESS rather than per venue spelling, so Templeton doesn't
  // offer itself three times. Every address seen for it is counted, because a
  // place is written with its address on some rows and without on others, and
  // the one most rows agree on is the one to show. `count` tallies the same
  // thing hostedByCounts (lib/useEvents) counts - events per hosted-by name -
  // kept as a running total in this same loop rather than a second pass
  // through that function, because search.ts has to stay Supabase-free (see
  // the file header) and useEvents.ts pulls in the browser Supabase client at
  // import time.
  //
  // hostedByNames is plural: a row can carry a venue AND an organiser layered
  // on top of it (Hill City Mahj at Palmera House), and both get a row here,
  // sharing the same city/address the venue itself carries - an organiser
  // doesn't have its own storefront, it runs its thing at the venue's.
  const places = new Map<string, { city: string; addresses: Map<string, number>; count: number }>()
  for (const e of events) {
    // Cached below `needle` on purpose: this per-row derivation (the
    // hosted-by/ALIASES lookup, the city and address reads) doesn't depend on
    // what's typed at all, so building `places` used to redo it for the WHOLE
    // set on every keystroke even though the map it builds is the same map
    // every time - only the filter below actually reads `needle`.
    const { names, city, addr } = placeDerivationFor(e)
    if (names.length === 0) continue
    for (const name of names) {
      let place = places.get(name)
      if (!place) {
        place = { city, addresses: new Map(), count: 0 }
        places.set(name, place)
      }
      place.count++
      if (addr) place.addresses.set(addr, (place.addresses.get(addr) ?? 0) + 1)
    }
  }

  const placeRows: SearchSuggestion[] = [...places.entries()]
    // Matched on the NAME alone. The address below is context, not a key.
    .filter(([name]) => normalizeForSearch(name).includes(needle))
    // Ranked by how many events the name accounts for, not alphabetically - a
    // host running a dozen events should beat a venue string with one, even
    // when the venue's name happens to sort earlier. Andrew's literal test is
    // "type the name (or a prefix) and find it," and alphabetical-only order
    // failed that for "hill" the moment enough one-off "Hill ..." venues
    // existed to fill a 6-row limit ahead of Hill City Mahj's dozen rows. Name
    // is still the tiebreak, so two places with the same count keep a stable,
    // readable order rather than shuffling with the data.
    .sort((a, b) => b[1].count - a[1].count || a[0].localeCompare(b[0]))
    .map(([name, place]) => ({
      value: name,
      kind: 'Place' as const,
      detail: placeDetail(place.city, bestAddress(place.addresses)),
    }))

  const seen = new Set<string>()
  const categoryRows: SearchSuggestion[] = []
  for (const e of events) {
    // Every kind the event carries, so "com" offers Comedy even when the only
    // comedy in the window is tagged onto a Live Music open mic.
    for (const kind of eventKinds(e)) {
      const label = hostLabel(kind)
      if (!label || !normalizeForSearch(label).includes(needle)) continue
      if (seen.has(label.toLowerCase())) continue
      seen.add(label.toLowerCase())
      categoryRows.push({ value: label, kind: 'Category' })
    }
  }
  categoryRows.sort((a, b) => a.value.localeCompare(b.value))

  return [...placeRows, ...categoryRows].slice(0, limit)
}

/**
 * The address a place's rows agree on, or null when they don't.
 *
 * A tie shows no address at all rather than a guess. Dragon Fire Games is why:
 * it carries one row at 3102 Memorial Ave and one at 408 Federal St, the Memorial
 * Ave shop has closed, and the newer row is the stale one - so neither "most
 * common" nor "most recent" picks the right one. Sending somebody to a shut
 * storefront is worse than telling them only which town it's in.
 */
function bestAddress(addresses: Map<string, number>): string | null {
  let best: string | null = null
  let bestCount = 0
  let tied = false
  for (const [addr, count] of addresses) {
    if (count > bestCount) {
      best = addr
      bestCount = count
      tied = false
    } else if (count === bestCount) {
      tied = true
    }
  }
  return tied ? null : best
}

function placeDetail(city: string, address: string | null): string {
  if (!address) return city
  // "305 N. Bridge St., Bedford" beside a Bedford label says Bedford twice.
  const trimmed = address.replace(new RegExp(`,\\s*${city}\\s*$`, 'i'), '').trim()
  return `${city} · ${trimmed || address}`
}
